import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { supabase } from '../../lib/supabase'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { Users, BookOpen, Award, TrendingUp, Calendar, DollarSign } from 'lucide-react'

interface DashboardMetrics {
  totalCandidates: number
  totalCourses: number
  totalAttempts: number
  passRate: number
  recentRevenue: number
}

interface RecentActivity {
  activity_id: string
  type: 'enrollment' | 'exam_completed' | 'certificate_issued'
  user_name: string
  course_name: string
  timestamp: string
}

export function TenantDashboardPage() {
  useDocumentTitle('管理画面')
  const navigate = useNavigate()
  const { user } = useAuth()
  const { currentTenant, memberships } = useTenant()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([])

  useEffect(() => {
    // テナント管理者権限チェック
    const isTenantAdmin = memberships.some(
      (m) => m.tenant_id === currentTenant?.tenant_id && m.role_in_tenant === 'tenant_admin'
    )

    if (!isTenantAdmin && memberships.length > 0) {
      navigate('/mypage', { replace: true })
      return
    }

    if (user && currentTenant && isTenantAdmin) {
      fetchDashboardData()
    }
  }, [user, currentTenant])

  const fetchDashboardData = async () => {
    if (!currentTenant) return

    try {
      setLoading(true)
      setError(null)

      // 受講者数（自テナントのentitlementsから重複排除）
      const { data: entitlementsData } = await supabase
        .from('entitlements')
        .select('user_id')
        .eq('tenant_id', currentTenant.tenant_id)
        .eq('status', 'active')

      const uniqueCandidates = new Set(entitlementsData?.map((e) => e.user_id) || [])
      const totalCandidates = uniqueCandidates.size

      // 講座数
      const { count: totalCourses } = await supabase
        .from('courses')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', currentTenant.tenant_id)

      // 試験受験数
      const { count: totalAttempts } = await supabase
        .from('exam_attempts')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', currentTenant.tenant_id)

      // 合格率
      const { count: passedAttempts } = await supabase
        .from('exam_attempts')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', currentTenant.tenant_id)
        .eq('passed', true)

      const passRate =
        totalAttempts && totalAttempts > 0
          ? Math.round((passedAttempts || 0) / totalAttempts * 100)
          : 0

      // 直近30日の売上（簡易版）
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

      const { data: recentOrders } = await supabase
        .from('orders')
        .select('amount')
        .eq('tenant_id', currentTenant.tenant_id)
        .eq('status', 'completed')
        .gte('purchased_at', thirtyDaysAgo.toISOString())

      const recentRevenue = recentOrders?.reduce((sum, order) => sum + order.amount, 0) || 0

      setMetrics({
        totalCandidates,
        totalCourses: totalCourses || 0,
        totalAttempts: totalAttempts || 0,
        passRate,
        recentRevenue,
      })

      // 最近のアクティビティ（簡易版: 最近の試験完了）
      const { data: recentAttemptsData } = await supabase
        .from('exam_attempts')
        .select(`
          attempt_id,
          submitted_at,
          passed,
          users_profile!inner(full_name),
          courses!inner(title)
        `)
        .eq('tenant_id', currentTenant.tenant_id)
        .eq('status', 'graded')
        .order('submitted_at', { ascending: false })
        .limit(5)

      const activities: RecentActivity[] = (recentAttemptsData || []).map((attempt: any) => ({
        activity_id: attempt.attempt_id,
        type: attempt.passed ? 'certificate_issued' : 'exam_completed',
        user_name: attempt.users_profile?.full_name || '不明',
        course_name: attempt.courses?.title || '不明',
        timestamp: attempt.submitted_at,
      }))

      setRecentActivities(activities)
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err)
      setError(err.message || 'データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <MainLayout>
        <Loading message="ダッシュボードを読み込み中..." />
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">テナント管理ダッシュボード</h1>
          <p className="mt-2 text-gray-600">{currentTenant?.name}</p>
        </div>

        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} onRetry={fetchDashboardData} />
          </div>
        )}

        {/* メトリクス */}
        {metrics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">受講者数</h3>
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics.totalCandidates}</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">講座数</h3>
                <BookOpen className="h-5 w-5 text-green-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics.totalCourses}</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">試験受験数</h3>
                <Award className="h-5 w-5 text-purple-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics.totalAttempts}</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">合格率</h3>
                <TrendingUp className="h-5 w-5 text-yellow-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics.passRate}%</p>
            </div>
          </div>
        )}

        {/* 最近の売上 */}
        {metrics && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <DollarSign className="h-6 w-6 text-gray-500" />
              <h2 className="text-lg font-semibold text-gray-900">直近30日の売上</h2>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              ¥{metrics.recentRevenue.toLocaleString()}
            </p>
          </div>
        )}

        {/* 最近のアクティビティ */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="h-6 w-6 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">最近のアクティビティ</h2>
          </div>

          {recentActivities.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              まだアクティビティがありません
            </p>
          ) : (
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div
                  key={activity.activity_id}
                  className="flex items-start gap-4 pb-4 border-b border-gray-200 last:border-0"
                >
                  <div className="flex-shrink-0">
                    {activity.type === 'certificate_issued' ? (
                      <Award className="h-5 w-5 text-green-600" />
                    ) : (
                      <BookOpen className="h-5 w-5 text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">{activity.user_name}</span>
                      {activity.type === 'certificate_issued' ? ' が ' : ' が試験を完了: '}
                      <span className="font-medium">{activity.course_name}</span>
                      {activity.type === 'certificate_issued' && ' に合格しました'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(activity.timestamp).toLocaleString('ja-JP')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* クイックアクション */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/tenant/courses')}
            className="p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition text-left"
          >
            <BookOpen className="h-6 w-6 text-blue-600 mb-2" />
            <h3 className="font-medium text-gray-900">講座管理</h3>
            <p className="text-sm text-gray-600">講座の作成・編集</p>
          </button>

          <button
            onClick={() => navigate('/tenant/candidates')}
            className="p-4 bg-green-50 hover:bg-green-100 rounded-lg transition text-left"
          >
            <Users className="h-6 w-6 text-green-600 mb-2" />
            <h3 className="font-medium text-gray-900">受講者管理</h3>
            <p className="text-sm text-gray-600">受講者名簿</p>
          </button>

          <button
            onClick={() => navigate('/tenant/results')}
            className="p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition text-left"
          >
            <Award className="h-6 w-6 text-purple-600 mb-2" />
            <h3 className="font-medium text-gray-900">試験結果</h3>
            <p className="text-sm text-gray-600">試験結果一覧</p>
          </button>
        </div>
      </div>
    </MainLayout>
  )
}
